/**
 * Floating damage number that pops above a damaged card mesh, rises,
 * and fades. Auto-removes from GameBoard state via onComplete after
 * the animation finishes.
 *
 * Spawned by GameBoard._spawnFloatingDamage when combat resolution
 * actually applies damage > 0 to a card (Minion currentLife loss,
 * Avatar player-life loss, Site player-life loss). The position is
 * captured ONCE at spawn time in viewport pixels (projected from the
 * mesh's top-center); the floater then animates purely via CSS
 * transform, so it doesn't need per-frame re-projection.
 *
 * Visual: bold gold-rimmed scarlet number, 3cqw font, drop shadow
 * for readability over any card art. Springs up + scales up briefly
 * then fades while continuing to drift. Roughly 900ms total.
 *
 * Props:
 *   x, y. Viewport pixels of the anchor (mesh top-center).
 *   value. Damage amount (always shown as -N).
 *   color. Text color; defaults to scarlet for damage.
 *   onComplete. Called when animation ends so GameBoard can
 *                drop the entry from state.
 *   viewScale. Canvas scale; CSS values are divided by it to
 *                land at the right viewport position inside
 *                .app-canvas's containing block.
 */
import { motion } from 'framer-motion';

// Above every prompt / modal layer (combat menus sit at 1100): damage
// feedback is transient and must never hide behind UI chrome.
const Z = 1300;

export default function FloatingDamageNumber({
  x, y, value, label = null, color = '#ef4444', onComplete, viewScale = 1,
}) {
  const scale = viewScale || 1;
  const cssLeft = x / scale;
  const cssTop  = y / scale;

  return (
    // Two-layer structure: the OUTER div owns the static centering
    // transform (bottom-center of the number pinned to the card's
    // top-center anchor); the INNER motion.div owns the animated
    // transform. framer-motion writes its own `transform` from the
    // y/scale motion values, so putting translate(-50%, -100%) on the
    // same element gets clobbered. The number then hangs off the
    // anchor's bottom-right instead of sitting over the card.
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        left: cssLeft,
        top:  cssTop,
        zIndex: Z,
        transform: 'translate(-50%, -100%)',
        pointerEvents: 'none',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 0, scale: 0.7 }}
        animate={{
          opacity: [0, 1, 1, 1, 0],
          y: [0, -2.2, -4, -5.2, -6.8].map((v) => `${v}cqw`),
          scale: [0.7, 1.5, 1.25, 1.15, 1.05],
        }}
        transition={{ duration: 1.6, times: [0, 0.12, 0.45, 0.75, 1], ease: 'easeOut' }}
        onAnimationComplete={onComplete}
        style={{
          color,
          fontFamily: "'FantaisieArtistique', cursive",
          fontSize: label ? '2.6cqw' : '5cqw',
          fontWeight: 900,
          letterSpacing: '0.02em',
          lineHeight: 1,
          // Layered text shadow: dark base for legibility on any art,
          // outer glow tinted to the damage color for game-y readability.
          textShadow: [
            '0 0.1cqw 0.3cqw rgba(0,0,0,0.95)',
            '0 0.05cqw 0.5cqw rgba(0,0,0,0.7)',
            `0 0 0.8cqw ${color}`,
          ].join(', '),
          pointerEvents: 'none',
          // Crisp render even with the cqw scale system. Let the GPU
          // composite this layer rather than re-rasterising per frame.
          willChange: 'transform, opacity',
        }}
      >
        {label ?? (value > 0 ? `-${value}` : value)}
      </motion.div>
    </div>
  );
}
